// FCM push notification helpers: permission, token registration, foreground msgs.

import { getToken, deleteToken, onMessage } from 'firebase/messaging';
import {
  getMessagingInstance,
  pushRegisterToken,
  pushDeleteToken,
} from './firebase';
import { getPlatformName } from './platform';

const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Browser permission status: 'granted' | 'denied' | 'default' | 'unsupported'.
export const getNotificationPermission = () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
};

// The Workbox SW (service-worker.js) is registered only in production. In dev
// no SW exists — detect that via getRegistration() and bail out early.
// Even with a registration present, the SW may be stuck in installing/waiting
// (e.g. first install on a slow network, or an update blocked by an open tab),
// so we still race serviceWorker.ready against a timeout.
const SW_READY_TIMEOUT_MS = 5000;
const getReadyRegistration = async () => {
  if (!('serviceWorker' in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration();
  if (!existing) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((resolve) =>
      setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS),
    ),
  ]);
};

// FCM background messages are handled by the single Workbox SW (service-worker.js),
// so the push token is bound to the registration that controls the page.
// Returns null when no service worker becomes active.
const getTokenWith = async (messaging) => {
  const registration = await getReadyRegistration();
  if (!registration) return null;
  return getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });
};

// Requests permission (must run from a user gesture), then registers the token.
export const enableNotifications = async () => {
  if (!vapidKey) throw new Error('Notifications are not configured');

  const messaging = await getMessagingInstance();
  if (!messaging)
    throw new Error('Notifications are not supported on this device');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted')
    throw new Error('Notification permission denied');

  const token = await getTokenWith(messaging);
  if (!token) throw new Error('Failed to obtain a notification token');

  await pushRegisterToken({ token, platform: getPlatformName() });
  return token;
};

// Removes the token locally and on the backend (logout / opt-out).
export const disableNotifications = async () => {
  if (!vapidKey) return;
  // Permission not granted → no live token can exist; avoid getToken() minting
  // a fresh one only to immediately delete it.
  if (getNotificationPermission() !== 'granted') return;

  const messaging = await getMessagingInstance();
  if (!messaging) return;

  const token = await getTokenWith(messaging);
  if (!token) return;

  await pushDeleteToken({ token });
  await deleteToken(messaging);
};

// Silent re-registration on each session start (FCM tokens rotate, esp. on iOS).
export const refreshTokenIfGranted = async () => {
  if (!vapidKey) return;
  if (getNotificationPermission() !== 'granted') return;

  const messaging = await getMessagingInstance();
  if (!messaging) return;

  const token = await getTokenWith(messaging);
  if (token) await pushRegisterToken({ token, platform: getPlatformName() });
};

// Foreground message handler: badge is kept in sync by the unread listener,
// so here we only surface the incoming message. Returns an unsubscribe fn.
export const listenForegroundMessages = async (onShow) => {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};

  return onMessage(messaging, (payload) => onShow?.(payload));
};
